import { TextField } from '@mui/material';
import { isAxiosError } from 'axios';
import { FormEvent, useEffect, useState } from 'react';

import {
    Actions,
    FieldGroup,
    Form,
    Modal,
    ModalHeader,
    ModalNotification,
    PrimaryButton,
    SecondaryButton,
    Section,
} from 'components/common/Modal';

import api from 'api';
import useTranslate from 'hooks/useTranslate';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ApiErrorResponse {
    msg?: string;
}

interface EmailRequiredToPromoteModalProps {
    open: boolean;
    userId: number;
    onClose: () => void;
    onEmailSaved: () => void | Promise<void>;
}

const EmailRequiredToPromoteModal = ({
    open,
    userId,
    onClose,
    onEmailSaved,
}: EmailRequiredToPromoteModalProps) => {
    const { translate } = useTranslate();
    const [email, setEmail] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setEmail('');
            setErrorMessage(null);
        }
    }, [open]);

    const trimmedEmail = email.trim();
    const emailIsRequired = trimmedEmail === '';
    const emailIsValid = !emailIsRequired && EMAIL_REGEX.test(trimmedEmail);
    const emailHelperText = emailIsRequired
        ? translate('emailRequired')
        : !emailIsValid
          ? translate('invalidEmail')
          : ' ';

    const handleClose = () => {
        if (!isSaving) {
            onClose();
        }
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!emailIsValid || isSaving) {
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);

        try {
            await api.admin.updateUserProfile(userId, { email: trimmedEmail });
            await onEmailSaved();
            onClose();
        } catch (error) {
            let message = translate('somethingWentWrong');

            if (isAxiosError<ApiErrorResponse>(error)) {
                if (error.response?.status === 409) {
                    message = translate('emailAlreadyInUse');
                } else if (error.response?.status === 403) {
                    message = translate('forbidden');
                } else if (error.response?.data?.msg) {
                    message = error.response.data.msg;
                }
            }

            setErrorMessage(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            size="sm"
            ariaLabel={translate('emailRequiredToPromoteAccount')}
        >
            <Form onSubmit={handleSubmit}>
                <ModalHeader
                    eyebrow={translate('activateAccount')}
                    title={translate('emailRequiredToPromoteAccount')}
                    subtitle={translate('emailRequiredToPromoteAccountDescription')}
                />

                <Section>
                    <FieldGroup>
                        <TextField
                            autoFocus
                            fullWidth
                            required
                            type="email"
                            label={translate('email')}
                            value={email}
                            onChange={(event) => {
                                setEmail(event.target.value);
                                setErrorMessage(null);
                            }}
                            error={!emailIsValid}
                            helperText={emailHelperText}
                            disabled={isSaving}
                        />
                    </FieldGroup>
                </Section>

                <Actions>
                    <SecondaryButton type="button" onClick={handleClose} disabled={isSaving}>
                        {translate('cancel')}
                    </SecondaryButton>
                    <PrimaryButton type="submit" disabled={!emailIsValid || isSaving}>
                        {isSaving ? translate('saving') : translate('saveAndActivateAccount')}
                    </PrimaryButton>
                </Actions>

                {errorMessage && (
                    <ModalNotification
                        variant="error"
                        title={translate('errorTitle')}
                        message={errorMessage}
                    />
                )}
            </Form>
        </Modal>
    );
};

export default EmailRequiredToPromoteModal;
